import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ErrorMessage, useFormikContext } from 'formik'
import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'

import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak'
import {
  RichTextEditor,
  type RichTextEditorRef,
  MenuControlsContainer,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonBulletedList,
  MenuButtonTextColor,
  MenuButtonHighlightColor,
  MenuSelectFontSize,
  MenuButtonEditLink,
  LinkBubbleMenu,
  MenuDivider,
  MenuSelectTextAlign,
  MenuButton,
} from 'mui-tiptap'
import { Placeholder } from '@tiptap/extensions'

import lang from '@/lang'
import {
  useAiTextTransform,
} from '@ai-text-transform-bridge'
import { isMeinBuero } from '@/utils/build-env'
import {
  FormatOption,
  getExtensions,
  formatsFull,
  TOOLBAR_INTERACTION_DELAY,
  hasBasicFormats,
  hasUndoRedoFormats,
  hasListFormats,
  isEditorEmpty,
  getInlineStyles,
  getNormalStyles,
  getMenuBarStyles,
  getRichTextFieldStyles,
  getProseMirrorStyles,
  getSlidingVariantStyles,
  isToolbarElement,
  createToolbarInteractionHandler,
  createClickOutsideHandler,
  FONT_SIZE_OPTIONS,
} from '@/modules/base/components/inputs/html-input/_helpers'
import EnhancedOrderedListMenuButton from '@/modules/base/components/inputs/html-input/extensions/enhanced-ordered-list/enhanced-ordered-list-menu-button'
import { PlaceholderMenuButton } from '@/modules/base/components/inputs/html-input/extensions/placeholder'
import { getPlaceholderEditorStyles } from '@/modules/base/components/inputs/html-input/extensions/placeholder/placeholder-styles'
import { MjmlButtonMenuButton } from '@/modules/base/components/inputs/html-input/extensions/mjml-button'
import { getMjmlButtonEditorStyles } from '@/modules/base/components/inputs/html-input/extensions/mjml-button/mjml-button-styles'
import { ButtonItem } from '@/modules/base/components/inputs/html-input/extensions/mjml-button/types'

const {
  emptyRequiredFieldErrorMessage: NOT_EMPTY_MESSAGE,
} = lang

interface PlaceholderItem {
  label: string
  value: string
}

interface HtmlInputV3Props {
  value?: string
  onFocus?: (event?: React.FocusEvent) => void
  onBlur?: (value: string, name: string) => void
  onChange?: (value: string, name: string) => void
  onReady?: (editor: import('@tiptap/react').Editor) => void
  name: string
  useFormik?: boolean
  errorMessage?: string
  required?: boolean
  formats?: FormatOption[]
  wrapperClass?: string
  readOnly?: boolean
  disabled?: boolean
  label?: string
  placeholder?: string
  inline?: boolean
  minHeight?: string
  className?: string
  focusBackground?: string
  background?: string
  defaultFontSize?: string
  dataQsId: string
  variant?: 'sliding' | 'absolute' | 'visible'
  excludedFormats?: FormatOption[]
  placeholders?: (string | PlaceholderItem)[]
  placeholderBrackets?: { open: string; close: string }
  buttons?: ButtonItem[]
  enableAiFeatures?: boolean
}

const useStyles = makeStyles(theme => ({
  htmlInput: {
    height: '100%',
    '&.inline': {
      marginBottom: 'unset',
    },
    '&.disabled, &.readOnly': {
      color: theme.disabledColor,
    },
  },
  wrapper: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: 12,
    color: theme.error,
    marginTop: 10,
  },
  label: {
    display: 'block',
    color: theme.text,
    fontSize: 13,
    marginBottom: 10,
    '&.disabled': {
      color: theme.disabledColor,
    },
  },
  colorTextInput: {
    '& input': {
      padding: '8px 14px !important',
    },
  },
}))

const isMB = isMeinBuero()

const HtmlInputV3: React.FC<HtmlInputV3Props> = (props) => {
  const {
    value = '',
    onFocus,
    onBlur,
    onChange,
    name = 'html',
    useFormik,
    errorMessage = '',
    required = false,
    formats = formatsFull,
    wrapperClass = '',
    readOnly = false,
    disabled = false,
    label = '',
    placeholder = '',
    inline = false,
    className,
    background,
    defaultFontSize,
    onReady: onReadyExtern,
    excludedFormats = ['placeholder', 'mjmlButton', 'pageBreak'],
    dataQsId,
    minHeight,
    variant = 'absolute',
    placeholders = [],
    placeholderBrackets = { open: '{{', close: '}}' },
    buttons = [],
    enableAiFeatures = true,
  } = props
  const theme = useTheme()

  const [initValue, setInitValue] = useState(value)
  const [error, setError] = useState(errorMessage)
  const [inFocus, setInFocus] = useState(false)

  const editorRef = useRef<RichTextEditorRef>(null)
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const isToolbarInteractionRef = useRef(false)
  const lastEmittedContentRef = useRef<string | null>(null)

  const classes = useStyles()

  const activeFormats = useMemo(() => formats.filter(format => !excludedFormats.includes(format)), [formats, excludedFormats])
  const formikContext = useFormik === undefined || useFormik ? useFormikContext() : null
  let errorEl = null
  let field
  let helpers
  if (formikContext) {
    field = formikContext.getFieldProps(name)
    helpers = formikContext.getFieldHelpers(name)
    errorEl = <ErrorMessage name={name} className={classes.error} component="div" />
  } else {
    errorEl = error ? <div className={classes.error}>{error}</div> : null
  }

  useEffect(() => {
    const newValue = formikContext ? (field?.value || '') : (value || '')
    if (newValue !== initValue) {
      setInitValue(newValue)
      // editor already has this content, so skip setContent to avoid resetting the cursor.
      if (newValue === lastEmittedContentRef.current) {
        return
      }
      const editor = editorRef.current?.editor
      if (editor && editor.getHTML() !== newValue) {
        editor.commands.setContent(newValue, { emitUpdate: false })
      }
    }
  }, [formikContext ? field?.value : value, initValue])

  const { renderAiComponents } = useAiTextTransform({
    containerRef: editorContainerRef,
    enabled: (!isMB && enableAiFeatures) && !readOnly && !disabled,
    onApplyResult: useCallback((result: string) => {
      const editor = editorRef.current?.editor
      if (!editor) return

      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertContent(result)
        .run()

      const data = editor.getHTML()
      if (helpers?.setValue) {
        helpers.setValue(data)
      }
      onChange?.(data, name)
    }, [helpers, onChange, name]),
  })

  const extensions = useMemo(() => {
    const placeholderConfig = placeholders.length > 0
      ? {
        types: placeholders,
        brackets: placeholderBrackets,
      }
      : undefined

    const mjmlButtonConfig = buttons.length > 0
      ? { buttons }
      : undefined

    return [
      ...getExtensions(activeFormats, excludedFormats, placeholderConfig, mjmlButtonConfig),
      Placeholder.configure({
        placeholder,
      }),
    ]
  }, [activeFormats, excludedFormats, placeholder, placeholders, placeholderBrackets, buttons])

  const handleChange = useCallback((content: string) => {
    lastEmittedContentRef.current = content

    if (helpers?.setValue) {
      helpers.setValue(content)
    }

    // Use editor reference to check if content is truly empty
    const editor = editorRef.current?.editor
    const isEmpty = isEditorEmpty(editor)

    if (required && isEmpty) {
      setError(NOT_EMPTY_MESSAGE)
    } else {
      setError('')
    }

    onChange?.(content, name)
  }, [helpers?.setValue, required, onChange, name])

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }

    setInFocus(true)
    onFocus?.()
  }, [onFocus])

  const handleBlurEditor = useCallback(() => {
    if (isToolbarInteractionRef.current) {
      isToolbarInteractionRef.current = false
      return
    }

    setInFocus(false)

    if (helpers?.setTouched) {
      helpers.setTouched(true, true)
    }

    const editor = editorRef.current?.editor
    const isEmpty = isEditorEmpty(editor)
    const content = isEmpty ? '' : (editor?.getHTML() || '')

    if (required && isEmpty) {
      setError(NOT_EMPTY_MESSAGE)
    }

    onBlur?.(content, name)
  }, [helpers?.setTouched, onBlur, name, required])

  const handleBlur = useCallback(({ event }) => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }

    // Check relatedTarget - this is where focus is going
    const relatedTarget = event.relatedTarget as HTMLElement | null
    if (relatedTarget && isToolbarElement(relatedTarget)) {
      return
    }
    if (relatedTarget && editorContainerRef.current?.contains(relatedTarget)) {
      return
    }

    // If relatedTarget is null (common with MUI dropdowns/popovers),
    // use a minimal timeout to check if it's a toolbar interaction
    if (!relatedTarget) {
      blurTimeoutRef.current = setTimeout(() => {
        if (isToolbarInteractionRef.current) {
          return
        }

        if (document.activeElement && isToolbarElement(document.activeElement as HTMLElement)) {
          return
        }

        handleBlurEditor()
      }, 10)
      return
    }

    handleBlurEditor()
  }, [handleBlurEditor])

  // Optimized click-outside detection using imported helper
  useEffect(() => {
    if (!inFocus) return

    const handleDocumentClick = createClickOutsideHandler(
      editorContainerRef,
      isToolbarInteractionRef,
      handleBlurEditor,
    )

    // Add both mouse and touch event listeners for mobile compatibility
    document.addEventListener('mousedown', handleDocumentClick, {
      passive: true,
      capture: true,
    })
    document.addEventListener('touchstart', handleDocumentClick, {
      passive: true,
      capture: true,
    })

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick, {
        capture: true,
      })
      document.removeEventListener('touchstart', handleDocumentClick, {
        capture: true,
      })
    }
  }, [inFocus, handleBlurEditor])

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  // Initialize editor with default settings when ready
  const handleEditorReady = useCallback(() => {
    const editor = editorRef.current?.editor

    if (editor) {
      // Set default font size if provided
      if (defaultFontSize) {
        editor.commands.setFontSize(defaultFontSize)
      }

      // Call external ready handler if provided
      onReadyExtern?.(editor)
    }
  }, [defaultFontSize, onReadyExtern])

  // Optimized toolbar interaction handler using imported helper
  const handleToolbarInteraction = useCallback(
    createToolbarInteractionHandler(isToolbarInteractionRef, TOOLBAR_INTERACTION_DELAY),
    [],
  )

  const renderToolbarComponents = useCallback((): React.ReactNode => {
    if (readOnly || disabled) return null
    return (
      <MenuControlsContainer
        onMouseDown={handleToolbarInteraction}
        onTouchStart={handleToolbarInteraction}
      >
        {/* Basic formatting */}
        {activeFormats.includes('bold') && <MenuButtonBold tooltipLabel={lang.htmlInput.tooltipLabels.bold} />}
        {activeFormats.includes('italic') && <MenuButtonItalic tooltipLabel={lang.htmlInput.tooltipLabels.italic} />}
        {activeFormats.includes('underline') && <MenuButtonUnderline tooltipLabel={lang.htmlInput.tooltipLabels.underline} />}

        {/* Font size */}
        {activeFormats.includes('fontSize') && (
          <MenuSelectFontSize
            options={FONT_SIZE_OPTIONS}
            unsetOptionLabel={lang.generics.default}
            tooltipTitle={lang.htmlInput.tooltipLabels.fontSize}
            sx={{
              '& .MuiSelect-select:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
              '& fieldset.MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
            }}
          />
        )}

        {/* Colors */}
        {activeFormats.includes('fontColor') && (
          <MenuButtonTextColor
            ColorPickerProps={{ classes: { colorTextInput: classes.colorTextInput } }}
            tooltipLabel={lang.htmlInput.tooltipLabels.fontColor}
            labels={lang.htmlInput.fontColor}
          />
        )}
        {activeFormats.includes('fontBackgroundColor') && (
          <MenuButtonHighlightColor
            tooltipShortcutKeys={[]}
            ColorPickerProps={{ classes: { colorTextInput: classes.colorTextInput } }}
            tooltipLabel={lang.htmlInput.tooltipLabels.fontBackgroundColor}
            labels={lang.htmlInput.fontBackgroundColor}
          />
        )}

        {/* Link */}
        {activeFormats.includes('link') && <MenuButtonEditLink tooltipLabel={lang.htmlInput.tooltipLabels.link} />}

        {/* Divider before undo/redo */}
        {hasBasicFormats(activeFormats) && hasUndoRedoFormats(activeFormats) && <MenuDivider />}

        {/* Undo/Redo */}
        {activeFormats.includes('undo') && <MenuButtonUndo tooltipLabel={lang.htmlInput.tooltipLabels.undo} />}
        {activeFormats.includes('redo') && <MenuButtonRedo tooltipLabel={lang.htmlInput.tooltipLabels.redo} />}

        {/* Alignment */}
        {activeFormats.includes('alignment') && (
          <>
            <MenuDivider />
            <MenuSelectTextAlign
              sx={{
                '& .MuiSelect-select:hover': {
                  background: 'rgba(0, 0, 0, 0.04)',
                },
                '& fieldset.MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              }}
              tooltipTitle={lang.htmlInput.tooltipLabels.alignment}
              emptyLabel={<FormatAlignLeftIcon fontSize="small" />}
              options={[
                {
                  value: 'left',
                  IconComponent: FormatAlignLeftIcon,
                  label: lang.htmlInput.tooltipLabels.alignmentOptions.left,
                },
                {
                  value: 'center',
                  IconComponent: FormatAlignCenterIcon,
                  label: lang.htmlInput.tooltipLabels.alignmentOptions.center,
                },
                {
                  value: 'right',
                  IconComponent: FormatAlignRightIcon,
                  label: lang.htmlInput.tooltipLabels.alignmentOptions.right,
                },
                {
                  value: 'justify',
                  IconComponent: FormatAlignJustifyIcon,
                  label: lang.htmlInput.tooltipLabels.alignmentOptions.justify,
                },
              ]}
            />
          </>
        )}

        {/* Lists */}
        {hasListFormats(activeFormats) && (
          <>
            <MenuDivider />
            {activeFormats.includes('bulletedList') && <MenuButtonBulletedList tooltipLabel={lang.htmlInput.tooltipLabels.bulletedList} />}
            {activeFormats.includes('numberedList') && <EnhancedOrderedListMenuButton tooltipTitle={lang.htmlInput.tooltipLabels.numberedList} />}
          </>
        )}

        {/* Page Break */}
        {activeFormats.includes('pageBreak') && (
          <>
            <MenuDivider />
            <MenuButton
              value="pageBreak"
              tooltipLabel={lang.htmlInput.tooltipLabels.pageBreak}
              onClick={() => {
                const editor = editorRef.current?.editor
                if (editor) {
                  editor.chain().focus().insertContent({
                    type: 'pageBreak',
                  }).run()
                }
              }}
            >
              <InsertPageBreakIcon fontSize='small' />
            </MenuButton>
          </>
        )}

        {/* Placeholder */}
        {(activeFormats.includes('placeholder') || placeholders.length > 0) && (
          <>
            <MenuDivider />
            <PlaceholderMenuButton
              placeholderTypes={placeholders}
              tooltipTitle={lang.htmlInput.tooltipLabels.placeholder}
            />
          </>
        )}

        {/* MJML Button */}
        {(activeFormats.includes('mjmlButton') || buttons.length > 0) && (
          <>
            <MenuDivider />
            <MjmlButtonMenuButton
              tooltipTitle={lang.htmlInput.tooltipLabels.mjmlButton}
              buttons={buttons}
            />
          </>
        )}
      </MenuControlsContainer>
    )
  }, [activeFormats, readOnly, disabled, buttons, placeholders, handleToolbarInteraction])

  const memoizedStyles = useMemo(() => ({
    richTextFieldStyles: getRichTextFieldStyles(inFocus, inline, theme, variant, background),
    inlineOrNormalStyles: inline ? getInlineStyles(inFocus, theme) : getNormalStyles(inFocus, theme),
    proseMirrorStyles: getProseMirrorStyles(inline, theme, minHeight),
    slidingVariantStyles: variant === 'sliding' ? getSlidingVariantStyles(theme) : {},
    menuBarStyles: getMenuBarStyles(variant, inFocus, theme),
    placeholderStyles: activeFormats.includes('placeholder') ? getPlaceholderEditorStyles(theme) : {},
    mjmlButtonStyles: activeFormats.includes('mjmlButton') ? getMjmlButtonEditorStyles(theme) : {},
  }), [inFocus, inline, theme, variant, minHeight, activeFormats, background])

  const classNames = useMemo(() => ({
    wrapper: clsx(classes.wrapper, { inline, readOnly, disabled }),
    htmlInput: clsx(classes.htmlInput, { inline, readOnly, disabled }),
    label: clsx(classes.label, { disabled: readOnly }),
  }), [classes, inline, readOnly, disabled])

  return (
    <div className={clsx(wrapperClass, className)} data-qs-id={dataQsId}>
      <div className={classNames.htmlInput}>
        {label && <label className={classNames.label}>{label}</label>}
        <div className={classNames.wrapper} ref={editorContainerRef}>
          <RichTextEditor
            ref={editorRef}
            content={initValue}
            extensions={extensions}
            editable={!readOnly && !disabled}
            onUpdate={({ editor }) => {
              // Bei leerem Editor leeren String verwenden
              if (isEditorEmpty(editor)) {
                handleChange('')
                return
              }
              let html = editor.getHTML()
              // Replace empty divs with br
              // Empty divs no longer have style attributes thanks to the modified Paragraph extension
              html = html.replace(/<div[^>]*>(?:\s*<br[^>]*class="ProseMirror-trailingBreak"[^>]*>)*\s*<\/div>/g, '<br />')
              // Remove trailing empty divs at the end of the content
              html = html.replace(/(<div[^>]*>\s*(?:<br[^>]*\/?>)?\s*<\/div>\s*)+$/gi, '')
              handleChange(html)
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onCreate={handleEditorReady}
            renderControls={renderToolbarComponents}
            RichTextFieldProps={{
              MenuBarProps: {
                ...((variant === 'absolute' || variant === 'visible') ? {} : { hide: !inFocus }),
                sx: memoizedStyles.menuBarStyles,
              },
              sx: {
                ...memoizedStyles.richTextFieldStyles,
                ...memoizedStyles.inlineOrNormalStyles,
                '& .ProseMirror': memoizedStyles.proseMirrorStyles,
                ...memoizedStyles.slidingVariantStyles,
                ...memoizedStyles.placeholderStyles,
                ...memoizedStyles.mjmlButtonStyles,
              },
            }}
          >
            {() => (
              <>
                {activeFormats.includes('link') && <LinkBubbleMenu labels={lang.htmlInput.link} />}
              </>
            )}
          </RichTextEditor>
        </div>
        {errorEl}
        {renderAiComponents()}
      </div>
    </div>
  )
}

export type { HtmlInputV3Props, FormatOption }

export default HtmlInputV3
